import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Navigation } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, STOPS, categoryById } from "@/lib/site";
import { EASE } from "@/components/Motion";

export default function RouteMap() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const visible = STOPS.filter((s) => filter === "all" || s.cat === filter);

  return (
    <div data-testid="route-map">
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          data-testid="map-filter-all"
          onClick={() => setFilter("all")}
          className={`rounded-full border px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
            filter === "all"
              ? "border-[#0B2B22] bg-[#0B2B22] text-[#F4F7F4]"
              : "border-[#154234]/25 bg-white/10 text-current hover:border-[#154234]/50"
          }`}
        >
          All stops
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`map-filter-${c.id}`}
            onClick={() => setFilter(c.id)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
              filter === c.id
                ? "border-[#0B2B22] bg-[#0B2B22] text-[#F4F7F4]"
                : "border-[#154234]/25 bg-white/10 text-current hover:border-[#154234]/50"
            }`}
          >
            <c.icon size={14} style={{ color: filter === c.id ? "#E05A47" : c.color }} />
            {c.label}
          </button>
        ))}
      </div>

      <div className="topo-bg relative aspect-[16/10] overflow-hidden rounded-[2rem] border border-[#154234]/15 shadow-[0_24px_60px_rgba(11,43,34,0.18)]">
        <svg
          viewBox="0 0 100 62"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          <path
            d="M2,50 C10,44 12,26 24,22 C33,19 36,38 46,40 C56,42 58,20 70,18 C80,16 84,32 98,30"
            fill="none"
            stroke="#154234"
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.8"
          />
          <path
            d="M46,40 C50,45 54,41 55,37 C56,33 57,31 58,30"
            fill="none"
            stroke="#E05A47"
            strokeWidth="1.6"
            strokeDasharray="3 3"
            strokeLinecap="round"
          />
        </svg>

        <div
          className="pin-pulse absolute size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E05A47] ring-4 ring-white/80"
          style={{ left: "2.5%", top: "80%" }}
          title="You are here"
        />

        <AnimatePresence>
          {visible.map((s, i) => {
            const cat = categoryById(s.cat);
            return (
              <motion.button
                key={s.id}
                data-testid={`map-pin-${s.cat}-${s.id}`}
                onClick={() => setSelected(s)}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${s.x}%`, top: `${s.y}%` }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 320, damping: 22, delay: i * 0.05 }}
                whileHover={{ scale: 1.18 }}
                whileTap={{ scale: 0.92 }}
                aria-label={s.name}
              >
                <span
                  className={`grid size-10 place-items-center rounded-full text-white shadow-lg ring-4 transition ${
                    selected?.id === s.id ? "ring-[#E05A47]" : "ring-white/70"
                  }`}
                  style={{ backgroundColor: cat.color }}
                >
                  <cat.icon size={18} />
                </span>
              </motion.button>
            );
          })}
        </AnimatePresence>

        <p className="absolute right-5 top-4 hidden font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#154234]/60 sm:block">
          VeloPaths curated route — Mile 0–12
        </p>

        <AnimatePresence>
          {selected && (
            <motion.div
              data-testid="map-stop-card"
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 26 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="absolute inset-x-4 bottom-4 rounded-2xl border border-[#154234]/10 bg-white/95 p-5 shadow-2xl backdrop-blur sm:left-6 sm:right-auto sm:max-w-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span
                    className="grid size-10 shrink-0 place-items-center rounded-xl text-white"
                    style={{ backgroundColor: categoryById(selected.cat).color }}
                  >
                    {(() => {
                      const Icon = categoryById(selected.cat).icon;
                      return <Icon size={18} />;
                    })()}
                  </span>
                  <div>
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#52635B]">
                      {categoryById(selected.cat).label} · Mile {(selected.id * 1.7).toFixed(1)}
                    </p>
                    <p className="font-display text-lg font-bold tracking-tight text-[#0B2B22]">
                      {selected.name}
                    </p>
                  </div>
                </div>
                <button
                  data-testid="map-stop-close"
                  onClick={() => setSelected(null)}
                  aria-label="Close stop details"
                  className="grid size-8 shrink-0 place-items-center rounded-full text-[#52635B] transition-colors hover:bg-[#F4F7F4]"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[#52635B]">{selected.blurb}</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-[#E05A47]/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-[#E05A47]">
                  {selected.tag}
                </span>
                <button
                  data-testid="map-stop-detour-btn"
                  onClick={() =>
                    toast(`Detour to ${selected.name}`, {
                      description: "In the app, one tap re-routes you there and back to the path.",
                    })
                  }
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#0B2B22] px-4 py-1.5 text-xs font-bold text-[#F4F7F4] transition-colors hover:bg-[#154234]"
                >
                  <Navigation size={12} /> Preview detour
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
